/**
 * Single source of truth for "what is missing in a grade".
 * Used by the grades list (counts + filter + index) and by the grade card
 * (red border + chips) so both always agree on which grades need fixing.
 *
 * Not every gap is equally urgent: a grade with no gate, no dismissal flow or
 * a class with no teacher cannot dismiss at all, while a grade with no
 * supervisor still works. The first group is critical (red), the second is a
 * warning (amber).
 */
export interface GradeIssues {
  gate: boolean;
  supervisors: boolean;
  teachers: boolean;
  flow: boolean;
  /** missing items that break dismissal, in display order */
  criticalKeys: string[];
  /** missing items worth flagging that still let dismissal run */
  warningKeys: string[];
  /** every missing item, criticals first */
  keys: string[];
  hasCritical: boolean;
  hasWarning: boolean;
  /** the red flag : only critical gaps outline a card in red */
  hasError: boolean;
}

export function getGradeIssues(grade: any): GradeIssues {
  const classes = grade?.school_classes || [];
  const gate = !(grade?.gates?.length > 0);
  const supervisors = !(grade?.supervisors?.length > 0);
  const teachers = classes.some((oneClass) => !(oneClass?.teachers?.length > 0));
  const flow = !grade?.flow?.name;

  const criticalKeys = [];
  if (gate) criticalKeys.push('gate');
  if (teachers) criticalKeys.push('teachers');
  if (flow) criticalKeys.push('flow');

  const warningKeys = [];
  if (supervisors) warningKeys.push('supervisors');

  return {
    gate,
    supervisors,
    teachers,
    flow,
    criticalKeys,
    warningKeys,
    keys: [...criticalKeys, ...warningKeys],
    hasCritical: criticalKeys.length > 0,
    hasWarning: warningKeys.length > 0,
    hasError: criticalKeys.length > 0
  };
}

/**
 * A flow is stored as one letter per stage ("PTSGP").
 * The first letter is always the request itself, so it is rendered as 'R'.
 * Same rule used by the dismissal flow tab.
 */
export function extractFlowStages(flowName: string): string[] {
  if (!flowName) {
    return [];
  }
  const stages = ['R'];
  for (let index = 1; index < flowName.length; index++) {
    stages.push(flowName[index]);
  }
  return stages;
}
